/**
 * Profile Editor Component
 *
 * Allows active users to edit their instructor profile
 * Loads existing data from HubSpot and allows updating all fields except email
 */

import { useState, useEffect } from 'preact/hooks';
import { profileService, VALID_SPECIALTIES } from '../../services/profileService';
import { LocationSelector } from '../common/LocationSelector';

interface ProfileEditorProps {
  onSaved?: () => void;
}

interface ProfileFormData {
  firstname: string;
  lastname: string;
  phone: string;
  country: string;
  state: string;
  city: string;
  address: string;
  escuela_de_formacion: string;
  ano_de_formacion: string;
  maestro_de_la_formacion: string;
  desarrollo_personal: boolean;
  specialties: string[];
  // Materials DAI
  has_crystals_bed: boolean;
  has_arrow: boolean;
  has_steel_bars: boolean;
  has_concrete_blocks: boolean;
  has_other: boolean;
  complemento_material_dai: string;
  // Providers
  provides_wood: boolean;
  provides_tepe: boolean;
  provides_iron_bar: boolean;
  provides_arrows: boolean;
  provides_wood_board: boolean;
  provides_concrete_block: boolean;
  nivel_de_experiencia_en_fuego: string;
  cuentanos_tu_experiencia: string;
}

export function ProfileEditor({ onSaved }: ProfileEditorProps) {
  // Loading and data state
  const [isLoading, setIsLoading] = useState(true);
  const [profileData, setProfileData] = useState<any>(null);

  // Form state
  const [formData, setFormData] = useState<ProfileFormData>({
    firstname: '',
    lastname: '',
    phone: '',
    country: '',
    state: '',
    city: '',
    address: '',
    escuela_de_formacion: '',
    ano_de_formacion: '',
    maestro_de_la_formacion: '',
    desarrollo_personal: false,
    specialties: [],
    // Materials DAI
    has_crystals_bed: false,
    has_arrow: false,
    has_steel_bars: false,
    has_concrete_blocks: false,
    has_other: false,
    complemento_material_dai: '',
    // Providers
    provides_wood: false,
    provides_tepe: false,
    provides_iron_bar: false,
    provides_arrows: false,
    provides_wood_board: false,
    provides_concrete_block: false,
    nivel_de_experiencia_en_fuego: '',
    cuentanos_tu_experiencia: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Load profile data on mount
  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    setIsLoading(true);
    setSubmitError(null);

    try {
      const response = await profileService.getMyData();

      if (response.success && response.data) {
        const data = response.data;
        setProfileData(data);

        // Map API data to form data
        setFormData({
          firstname: data.basic_info?.firstname || '',
          lastname: data.basic_info?.lastname || '',
          phone: data.basic_info?.phone || '',
          country: data.location?.country || '',
          state: data.location?.state || '',
          city: data.location?.city || '',
          address: data.location?.address || '',
          escuela_de_formacion: data.certification?.school || '',
          ano_de_formacion: data.certification?.year || '',
          maestro_de_la_formacion: data.certification?.master || '',
          desarrollo_personal: data.professional?.is_professional || false,
          specialties: Array.isArray(data.professional?.specialties) ? data.professional.specialties : [],
          // Materials DAI
          has_crystals_bed: data.materials?.has_crystals_bed || false,
          has_arrow: data.materials?.has_arrow || false,
          has_steel_bars: data.materials?.has_steel_bars || false,
          has_concrete_blocks: data.materials?.has_concrete_blocks || false,
          has_other: data.materials?.has_other || false,
          complemento_material_dai: data.materials?.other_material || '',
          // Providers
          provides_wood: data.providers?.provides_wood || false,
          provides_tepe: data.providers?.provides_tepe || false,
          provides_iron_bar: data.providers?.provides_iron_bar || false,
          provides_arrows: data.providers?.provides_arrows || false,
          provides_wood_board: data.providers?.provides_wood_board || false,
          provides_concrete_block: data.providers?.provides_concrete_block || false,
          nivel_de_experiencia_en_fuego: data.professional?.nivel_de_experiencia_en_fuego || '',
          cuentanos_tu_experiencia: data.professional?.story || '',
        });
      } else {
        setSubmitError(response.error || 'Error al cargar datos del perfil');
      }
    } catch (error) {
      console.error('Load profile error:', error);
      setSubmitError(error instanceof Error ? error.message : 'Error al cargar datos del perfil');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: Event) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const { name, value, type } = target;

    if (type === 'checkbox') {
      const checked = (target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Clear success message when user makes changes
    if (successMessage) {
      setSuccessMessage(null);
    }
  };

  // Handle specialty checkbox changes (multi-select)
  const handleSpecialtyChange = (specialty: string, checked: boolean) => {
    setFormData(prev => {
      const currentSpecialties = prev.specialties || [];
      if (checked) {
        // Add specialty if not already present
        if (!currentSpecialties.includes(specialty)) {
          return { ...prev, specialties: [...currentSpecialties, specialty] };
        }
      } else {
        // Remove specialty
        return { ...prev, specialties: currentSpecialties.filter(s => s !== specialty) };
      }
      return prev;
    });

    // Clear success message
    if (successMessage) {
      setSuccessMessage(null);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields (email is not editable, so not validated)
    if (!formData.firstname?.trim()) errors.firstname = 'El nombre es obligatorio';
    if (!formData.lastname?.trim()) errors.lastname = 'Los apellidos son obligatorios';
    if (!formData.country?.trim()) errors.country = 'El país es obligatorio';
    if (!formData.state?.trim()) errors.state = 'La provincia/estado es obligatoria';
    if (!formData.city?.trim()) errors.city = 'La ciudad es obligatoria';
    if (!formData.escuela_de_formacion?.trim()) errors.escuela_de_formacion = 'La escuela de certificación es obligatoria';

    // Year validation
    if (!formData.ano_de_formacion?.trim()) {
      errors.ano_de_formacion = 'El año de certificación es obligatorio';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCancel = () => {
    // Reload original data
    if (profileData) {
      loadProfileData();
      setSuccessMessage(null);
      setSubmitError(null);
      setFieldErrors({});
    }
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();

    setSubmitError(null);
    setSuccessMessage(null);

    // Validate form
    if (!validateForm()) {
      setSubmitError('Por favor, completa todos los campos obligatorios');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);

    try {
      // Send only editable fields (no email, no files)
      const response = await profileService.updateProfile(formData);

      if (response.success) {
        setSuccessMessage('Perfil actualizado exitosamente');
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Reload updated data
        await loadProfileData();

        // Call callback if provided
        if (onSaved) {
          onSaved();
        }
      } else {
        setSubmitError(response.error || 'Error al actualizar el perfil');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitError(error instanceof Error ? error.message : 'Error al actualizar el perfil');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div class="flex items-center justify-center min-h-screen">
        <div class="text-center">
          <svg class="animate-spin h-12 w-12 mx-auto text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p class="mt-4 text-gray-600">Cargando datos del perfil...</p>
        </div>
      </div>
    );
  }

  return (
    <div class="space-y-6">
      <form onSubmit={handleSubmit} class="space-y-8">

        {/* Success Alert */}
        {successMessage && (
          <div class="bg-green-50 border-l-4 border-green-400 p-4 rounded">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm text-green-700">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Alert */}
        {submitError && (
          <div class="bg-red-50 border-l-4 border-red-400 p-4 rounded">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm text-red-700">{submitError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Section 1: Basic Information */}
        <div class="bg-white shadow sm:rounded-lg p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Datos Básicos</h2>
          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Email - Read-only display */}
            <div class="sm:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div class="flex items-center">
                <svg class="h-5 w-5 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <span class="text-gray-900 font-medium">{profileData?.basic_info?.email || 'No disponible'}</span>
              </div>
              <p class="mt-1 text-xs text-gray-500">
                El email no puede modificarse. Contacta con soporte si necesitas cambiarlo.
              </p>
            </div>

            <div>
              <label htmlFor="firstname" class="block text-sm font-medium text-gray-700">
                Nombre <span class="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="firstname"
                name="firstname"
                value={formData.firstname}
                onInput={handleInputChange}
                required
                class={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  fieldErrors.firstname
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              {fieldErrors.firstname && (
                <p class="mt-1 text-sm text-red-600">{fieldErrors.firstname}</p>
              )}
            </div>

            <div>
              <label htmlFor="lastname" class="block text-sm font-medium text-gray-700">
                Apellidos <span class="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="lastname"
                name="lastname"
                value={formData.lastname}
                onInput={handleInputChange}
                required
                class={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  fieldErrors.lastname
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              {fieldErrors.lastname && (
                <p class="mt-1 text-sm text-red-600">{fieldErrors.lastname}</p>
              )}
            </div>

            <div class="sm:col-span-2">
              <label htmlFor="phone" class="block text-sm font-medium text-gray-700">
                Teléfono
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onInput={handleInputChange}
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        {/* Section 2: Location */}
        <div class="bg-white shadow sm:rounded-lg p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Ubicación</h2>
          <div class="space-y-6">
            {/* Cascading Location Selector */}
            <LocationSelector
              countryValue={formData.country}
              stateValue={formData.state}
              cityValue={formData.city}
              onCountryChange={(countryName) => {
                setFormData(prev => ({
                  ...prev,
                  country: countryName,
                  state: '',
                  city: ''
                }));
                // Clear related errors
                if (fieldErrors.country || fieldErrors.state || fieldErrors.city) {
                  setFieldErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.country;
                    delete newErrors.state;
                    delete newErrors.city;
                    return newErrors;
                  });
                }
                // Clear success message
                if (successMessage) {
                  setSuccessMessage(null);
                }
              }}
              onStateChange={(stateName) => {
                setFormData(prev => ({
                  ...prev,
                  state: stateName,
                  city: ''
                }));
                // Clear related errors
                if (fieldErrors.state || fieldErrors.city) {
                  setFieldErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.state;
                    delete newErrors.city;
                    return newErrors;
                  });
                }
                // Clear success message
                if (successMessage) {
                  setSuccessMessage(null);
                }
              }}
              onCityChange={(cityName) => {
                setFormData(prev => ({
                  ...prev,
                  city: cityName
                }));
                // Clear city error
                if (fieldErrors.city) {
                  setFieldErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.city;
                    return newErrors;
                  });
                }
                // Clear success message
                if (successMessage) {
                  setSuccessMessage(null);
                }
              }}
              errors={{
                country: fieldErrors.country,
                state: fieldErrors.state,
                city: fieldErrors.city
              }}
              required={{
                country: true,
                state: true,
                city: true
              }}
            />

            {/* Address - separate field */}
            <div>
              <label htmlFor="address" class="block text-sm font-medium text-gray-700">
                Dirección (privada)
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onInput={handleInputChange}
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        {/* Section 3: Certification */}
        <div class="bg-white shadow sm:rounded-lg p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Certificación</h2>
          <div class="space-y-6">
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="escuela_de_formacion" class="block text-sm font-medium text-gray-700">
                  Escuela de Certificación <span class="text-red-500">*</span>
                </label>
                <select
                  id="escuela_de_formacion"
                  name="escuela_de_formacion"
                  value={formData.escuela_de_formacion}
                  onChange={handleInputChange}
                  required
                  class={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                    fieldErrors.escuela_de_formacion
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                >
                  <option value="">Selecciona una escuela</option>
                  <option value="InnerFire">InnerFire</option>
                  <option value="The FIT">The FIT</option>
                  <option value="FIRE">FIRE</option>
                  <option value="Firewalking American Institute">Firewalking American Institute</option>
                  <option value="Firewalking Center">Firewalking Center</option>
                  <option value="Sudor">Sudor</option>
                </select>
                {fieldErrors.escuela_de_formacion && (
                  <p class="mt-1 text-sm text-red-600">{fieldErrors.escuela_de_formacion}</p>
                )}
              </div>

              <div>
                <label htmlFor="ano_de_formacion" class="block text-sm font-medium text-gray-700">
                  Año de Certificación <span class="text-red-500">*</span>
                </label>
                <select
                  id="ano_de_formacion"
                  name="ano_de_formacion"
                  value={formData.ano_de_formacion}
                  onChange={handleInputChange}
                  required
                  class={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                    fieldErrors.ano_de_formacion
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                >
                  <option value="">Selecciona el año</option>
                  {Array.from(
                    { length: new Date().getFullYear() - 1950 + 1 },
                    (_, i) => new Date().getFullYear() - i
                  ).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                {fieldErrors.ano_de_formacion && (
                  <p class="mt-1 text-sm text-red-600">{fieldErrors.ano_de_formacion}</p>
                )}
              </div>

              <div class="sm:col-span-2">
                <label htmlFor="maestro_de_la_formacion" class="block text-sm font-medium text-gray-700">
                  Maestro/Instructor que Certificó
                </label>
                <input
                  type="text"
                  id="maestro_de_la_formacion"
                  name="maestro_de_la_formacion"
                  value={formData.maestro_de_la_formacion}
                  onInput={handleInputChange}
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Display uploaded files (read-only) */}
            <div class="border-t border-gray-200 pt-6 space-y-6">
              {/* Profile photo */}
              {profileData?.certification?.photo_url ? (
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Foto de Perfil</label>
                  <div class="flex items-center space-x-4">
                    <img
                      src={profileData.certification.photo_url}
                      alt="Foto de perfil"
                      class="h-24 w-24 rounded-full object-cover border-2 border-gray-200"
                    />
                    <div class="text-sm text-gray-600">
                      <p>Foto actual subida correctamente.</p>
                      <p class="mt-1">Para cambiar tu foto, contacta con soporte.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Foto de Perfil</label>
                  <p class="text-sm text-gray-500">No has subido una foto de perfil.</p>
                </div>
              )}

              {/* Certificate */}
              {profileData?.certification?.certificate_url ? (
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Certificado</label>
                  <div class="flex items-start space-x-2">
                    <svg class="h-5 w-5 text-gray-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
                    </svg>
                    <div>
                      <a
                        href={profileData.certification.certificate_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Ver certificado actual →
                      </a>
                      <p class="mt-1 text-sm text-gray-600">
                        Para cambiar tu certificado, contacta con soporte.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Certificado</label>
                  <p class="text-sm text-gray-500">No has subido un certificado.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 4: Professional Development */}
        <div class="bg-white shadow sm:rounded-lg p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Desarrollo Personal y Profesional</h2>
          <div class="space-y-6">
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                  id="desarrollo_personal"
                  name="desarrollo_personal"
                  type="checkbox"
                  checked={formData.desarrollo_personal}
                  onChange={handleInputChange}
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div class="ml-3 text-sm">
                <label htmlFor="desarrollo_personal" class="font-medium text-gray-700">
                  ¿Eres profesional del Desarrollo Personal?
                </label>
              </div>
            </div>

            {formData.desarrollo_personal && (
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-3">
                  Especialidades
                </label>
                <div class="space-y-2">
                  {VALID_SPECIALTIES.map((specialty) => (
                    <div key={specialty} class="flex items-start">
                      <div class="flex items-center h-5">
                        <input
                          id={`specialty_${specialty}`}
                          type="checkbox"
                          checked={formData.specialties?.includes(specialty) || false}
                          onChange={(e) => handleSpecialtyChange(specialty, (e.target as HTMLInputElement).checked)}
                          class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>
                      <div class="ml-3 text-sm">
                        <label htmlFor={`specialty_${specialty}`} class="text-gray-700">
                          {specialty}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label htmlFor="nivel_de_experiencia_en_fuego" class="block text-sm font-medium text-gray-700">
                Experiencia con Fuego
              </label>
              <select
                id="nivel_de_experiencia_en_fuego"
                name="nivel_de_experiencia_en_fuego"
                value={formData.nivel_de_experiencia_en_fuego}
                onChange={handleInputChange}
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="">Selecciona una opción</option>
                <option value="Cerilla">Cerilla</option>
                <option value="Brasero">Brasero</option>
                <option value="Horno">Horno</option>
                <option value="Vulcano">Vulcano</option>
              </select>
            </div>

            <div>
              <label htmlFor="cuentanos_tu_experiencia" class="block text-sm font-medium text-gray-700">
                Cuéntanos tu Experiencia
              </label>
              <textarea
                id="cuentanos_tu_experiencia"
                name="cuentanos_tu_experiencia"
                rows={5}
                value={formData.cuentanos_tu_experiencia}
                onInput={handleInputChange}
                placeholder="Comparte tu experiencia como instructor de Firewalking y DAI..."
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        {/* Section 5: DAI Materials */}
        <div class="bg-white shadow sm:rounded-lg p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Material DAI</h2>
          <p class="text-sm text-gray-600 mb-4">
            Selecciona los materiales DAI que tienes disponibles:
          </p>
          <div class="space-y-4">
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                  id="has_crystals_bed"
                  name="has_crystals_bed"
                  type="checkbox"
                  checked={formData.has_crystals_bed}
                  onChange={handleInputChange}
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div class="ml-3 text-sm">
                <label htmlFor="has_crystals_bed" class="font-medium text-gray-700">
                  Cama de Cristales
                </label>
              </div>
            </div>

            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                  id="has_arrow"
                  name="has_arrow"
                  type="checkbox"
                  checked={formData.has_arrow}
                  onChange={handleInputChange}
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div class="ml-3 text-sm">
                <label htmlFor="has_arrow" class="font-medium text-gray-700">
                  Flechas
                </label>
              </div>
            </div>

            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                  id="has_steel_bars"
                  name="has_steel_bars"
                  type="checkbox"
                  checked={formData.has_steel_bars}
                  onChange={handleInputChange}
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div class="ml-3 text-sm">
                <label htmlFor="has_steel_bars" class="font-medium text-gray-700">
                  Barras de Acero
                </label>
              </div>
            </div>

            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                  id="has_concrete_blocks"
                  name="has_concrete_blocks"
                  type="checkbox"
                  checked={formData.has_concrete_blocks}
                  onChange={handleInputChange}
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div class="ml-3 text-sm">
                <label htmlFor="has_concrete_blocks" class="font-medium text-gray-700">
                  Bloques de Hormigón
                </label>
              </div>
            </div>

            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                  id="has_other"
                  name="has_other"
                  type="checkbox"
                  checked={formData.has_other}
                  onChange={handleInputChange}
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div class="ml-3 text-sm">
                <label htmlFor="has_other" class="font-medium text-gray-700">
                  Otro Material
                </label>
              </div>
            </div>

            {formData.has_other && (
              <div class="ml-7">
                <label htmlFor="complemento_material_dai" class="block text-sm font-medium text-gray-700">
                  Especifica el material
                </label>
                <input
                  type="text"
                  id="complemento_material_dai"
                  name="complemento_material_dai"
                  value={formData.complemento_material_dai}
                  onInput={handleInputChange}
                  placeholder="Describe el otro material DAI que tienes..."
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            )}
          </div>
        </div>

        {/* Section 6: Supplier */}
        <div class="bg-white shadow sm:rounded-lg p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Proveedor de Materiales</h2>
          <p class="text-sm text-gray-600 mb-4">
            Marca los materiales que puedes proveer a otros instructores:
          </p>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                  id="provides_wood"
                  name="provides_wood"
                  type="checkbox"
                  checked={formData.provides_wood}
                  onChange={handleInputChange}
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div class="ml-3 text-sm">
                <label htmlFor="provides_wood" class="font-medium text-gray-700">Leña</label>
              </div>
            </div>

            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                  id="provides_tepe"
                  name="provides_tepe"
                  type="checkbox"
                  checked={formData.provides_tepe}
                  onChange={handleInputChange}
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div class="ml-3 text-sm">
                <label htmlFor="provides_tepe" class="font-medium text-gray-700">Tepe</label>
              </div>
            </div>

            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                  id="provides_iron_bar"
                  name="provides_iron_bar"
                  type="checkbox"
                  checked={formData.provides_iron_bar}
                  onChange={handleInputChange}
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div class="ml-3 text-sm">
                <label htmlFor="provides_iron_bar" class="font-medium text-gray-700">Barra de Hierro</label>
              </div>
            </div>

            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                  id="provides_arrows"
                  name="provides_arrows"
                  type="checkbox"
                  checked={formData.provides_arrows}
                  onChange={handleInputChange}
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div class="ml-3 text-sm">
                <label htmlFor="provides_arrows" class="font-medium text-gray-700">Flechas</label>
              </div>
            </div>

            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                  id="provides_wood_board"
                  name="provides_wood_board"
                  type="checkbox"
                  checked={formData.provides_wood_board}
                  onChange={handleInputChange}
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div class="ml-3 text-sm">
                <label htmlFor="provides_wood_board" class="font-medium text-gray-700">Tabla de Madera</label>
              </div>
            </div>

            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                  id="provides_concrete_block"
                  name="provides_concrete_block"
                  type="checkbox"
                  checked={formData.provides_concrete_block}
                  onChange={handleInputChange}
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div class="ml-3 text-sm">
                <label htmlFor="provides_concrete_block" class="font-medium text-gray-700">Bloque de Hormigón</label>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div class="bg-white shadow sm:rounded-lg p-6">
          <div class="flex items-center justify-between">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}
              class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              class={`
                inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white
                ${isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }
              `}
            >
              {isSubmitting ? (
                <>
                  <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Guardando...
                </>
              ) : (
                'Guardar Cambios'
              )}
            </button>
          </div>

          {/* Required fields note */}
          <div class="mt-4 text-sm text-gray-600 text-center">
            <span class="text-red-500">*</span> Campos obligatorios
          </div>
        </div>
      </form>
    </div>
  );
}
